import * as React from 'react';
import { graphql } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';

import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import { getTemplateById, getCurrentUser, getDomainRestrictedTemplates } from 'graphql/operations';
import { getIdFromUrlParams, isAssessmentReadOnly as isAssessmentReadOnlySelector } from 'selectors/assessment/selector';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';

import { useTranslation, withTranslation } from 'react-i18next';
import { Paper } from '@material-ui/core';
import './NewRecord.scss';
import LoadingPage from 'components/common/Loader/Spinner/Spinner';
import { decodeHtml } from 'utils/htmlEncoderDecoder';
import { getLangFromUrlParams, getTemplateLanguages } from 'selectors/templates/selector';
import { showInfoMessage } from 'components/common/Alert/Alert';
import { upperFirst } from 'lodash';
import { isFetchingTemplate, getTemplate } from './selector';
import DdeWrapper from './DdeWrapper';

export const NewRecordBase = props => {
  const {
    history,
    template,
    languageCode,
    templateLanguages,
    isLoading,
    templateError,
    incrementAttachmentUploadCount,
    decrementAttachmentUploadCount
  } = props;
  const { t: translate } = useTranslation();

  const templateId = template?.id;
  const recordId = template?.recordId;
  const isDeleted = template?.isDeleted;
  const isUpdated = template?.isUpdated;
  const defaultLanguageCode = templateLanguages[0]?.languageCode;

  const isLanguageCodeAvailable = () => {
    let isAvailable = true;

    if (templateLanguages != null && templateLanguages.length > 0 && languageCode != null) {
      isAvailable = templateLanguages.some(lang => lang.languageCode.toLowerCase() === languageCode.toLowerCase());
    } else if (languageCode == null) {
      isAvailable = false;
    }

    return isAvailable;
  };

  const getInfoMessage = () => {
    if (!templateLanguages || templateLanguages.length === 0) {
      return null;
    }

    const languagesCount = templateLanguages.length;
    const defaultLanguage = upperFirst(templateLanguages[0]?.language);

    // when template is in single language
    if (languagesCount === 1) {
      return translate('newRecord.infoMessage.message', { defaultLanguage });
    }

    const lastLanguage = upperFirst(templateLanguages[languagesCount - 1].language);
    const languages1 = upperFirst(templateLanguages[1]?.language);

    // when template is in multiple(2) languages
    if (languagesCount === 2) {
      return translate('newRecord.infoMessage.message', { defaultLanguage }) + translate('newRecord.infoMessage.message1', { languages1 });
    }

    // when template is in multiple(more than 2) languages
    const middleLanguages = templateLanguages
      .slice(1, languagesCount - 1)
      .map(item => upperFirst(item.language))
      .join(', ');

    return (
      translate('newRecord.infoMessage.message', { defaultLanguage }) +
      translate('newRecord.infoMessage.message1', { languages1: middleLanguages }) +
      translate('newRecord.infoMessage.message2', { languages2: lastLanguage })
    );
  };

  const addLangCodeAndUpdateHistory = () => {
    const currentPath = window.location.pathname;

    const urlParts = currentPath.split('/');

    const modifiedURLParts = [...urlParts];
    const isLangAvailable = isLanguageCodeAvailable();

    let newModifiedURL = currentPath;
    if (modifiedURLParts?.length >= 3) {
      modifiedURLParts.splice(3, modifiedURLParts.length - 3, isLangAvailable ? languageCode : defaultLanguageCode);
      newModifiedURL = modifiedURLParts.join('/');
    }

    window.history.replaceState({}, '', newModifiedURL);
    if (!isLangAvailable && languageCode != null) {
      showInfoMessage(getInfoMessage(), {
        autoHideDuration: 10000
      });
    }
  };

  React.useEffect(() => {
    addLangCodeAndUpdateHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode, defaultLanguageCode]);

  React.useEffect(() => {
    if (templateId && recordId) {
      if (isDeleted && !isUpdated) {
        history.push('/template-not-found');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, templateId, recordId, isDeleted, isUpdated]);

  if (isLoading) {
    return <LoadingPage size="lg" expanded />;
  }

  if (templateError?.error) {
    return <div className="assessment-error">{translate('assessment.error')}</div>;
  }

  if (!template) {
    return <div className="assessment-error">{translate('assessment.notFound')}</div>;
  }

  return (
    <div className="new-record-container">
      <div className="assessment-page assessment-row">
        <section className="col-sm-8 new-record-section">
          <Paper elevation={2} className="paper-style new-record-form-container">
            <div data-testid="new-record-title" className="new-record-title">
              {translate('newRecord.recordTitle', { templateName: decodeHtml(template?.name) })}
            </div>
            <div className="questions-section">
              <DdeWrapper
                template={template}
                templateLanguages={templateLanguages}
                incrementAttachmentUploadCount={incrementAttachmentUploadCount}
                decrementAttachmentUploadCount={decrementAttachmentUploadCount}
              />
            </div>
          </Paper>
        </section>
      </div>
    </div>
  );
};

export const NewRecordHOC = compose(
  withTranslation(),
  withRouter,
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  graphql(getDomainRestrictedTemplates, { name: 'domainRestrictedTemplates' }),
  graphql(getTemplateById, {
    options: props => ({
      variables: {
        id: getIdFromUrlParams(props),
        languageCode: getLangFromUrlParams(props)
      },
      fetchPolicy: NETWORK_ONLY
    })
  }),
  mapProps(props => {
    const template = getTemplate(props);
    return {
      t: props.t,
      history: props.history,
      template,
      templateError: props.data.error,
      isLoading: isFetchingTemplate(props),
      currentUser: getCurrentUserSelector(props),
      isAssessmentReadOnly: isAssessmentReadOnlySelector({ template }),
      languageCode: getLangFromUrlParams(props),
      templateLanguages: getTemplateLanguages(props)
    };
  })
);

export const NewRecord = NewRecordHOC(NewRecordBase);
